import { Outlet, RootRoute, Route, Router, redirect, useLocation } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthGuard } from '@/components/AuthGuard'
import { CallbackPage } from '@/components/CallbackPage'
import { GlobalMenuBar } from '@/components/framework'
import {
  RequestsPage,
  ResourcesActivityLogPage,
  ResourcesSchedulePage,
  ShiftsCalendarPage,
  TimelinePage,
  TimelinePrototypePage,
  StationManagerPage,
} from '@/components/modules'
import { trackPageView } from '@/lib/analytics'
import { selectSelectedStationCode, useStationStore } from '@/lib/stores/station'

export const PageViewTracker = () => {
  const location = useLocation()
  const stationCode = useStationStore(selectSelectedStationCode)
  const previousViewRef = useRef<{ path: string; stationCode: string } | null>(null)

  useEffect(() => {
    const pageTitles: Record<string, string> = {
      '/shift-timeline': 'Timeline',
      '/shift-timeline-prototype': 'Timeline prototype',
      '/requests': 'Requests',
      '/shifts': 'Shifts calendar',
      '/shifts/default': 'Default schedule',
      '/shifts/override': 'Schedule override',
      '/shifts/activity-log': 'Shifts activity log',
      '/station-manager': 'Configuration',
      '/callback': 'Signing in',
    }
    const pageTitle = pageTitles[location.pathname] ?? 'RRMS'
    document.title = pageTitle === 'RRMS' ? pageTitle : `${pageTitle} | RRMS`

    const pagePath = location.pathname

    if (
      !window.gtag ||
      (previousViewRef.current?.path === pagePath &&
        previousViewRef.current.stationCode === stationCode)
    ) {
      return
    }

    previousViewRef.current = { path: pagePath, stationCode }

    trackPageView(pagePath, document.title)
  }, [location.pathname, stationCode])

  return null
}

const RouteFocusManager = () => {
  const location = useLocation()
  const previousPathRef = useRef(location.pathname)

  useEffect(() => {
    if (previousPathRef.current === location.pathname) return
    previousPathRef.current = location.pathname

    window.requestAnimationFrame(() => {
      const main = document.getElementById('main-content')
      const pageHeading = main?.querySelector<HTMLElement>('h1')
      if (pageHeading) {
        pageHeading.tabIndex = -1
        pageHeading.focus()
        return
      }
      main?.focus()
    })
  }, [location.pathname])

  return null
}

const SkipLink = () => {
  const { t } = useTranslation()
  return (
    <a
      href="#main-content"
      className="fixed top-2 left-2 z-100 -translate-y-20 rounded bg-white px-4 py-2 font-semibold text-primary shadow-lg transition-transform focus:translate-y-0"
    >
      {t('nav.skipToMain')}
    </a>
  )
}

const rootRoute = new RootRoute({
  component: () => (
    <>
      <PageViewTracker />
      <Outlet />
    </>
  ),
})

const authenticatedRoute = new Route({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: () => (
    <AuthGuard>
      <div className="m-0 flex h-screen w-full flex-col p-0">
        <SkipLink />
        <GlobalMenuBar />
        <RouteFocusManager />
        <main
          id="main-content"
          tabIndex={-1}
          className="min-h-0 flex-1 overflow-y-scroll focus:outline-none"
        >
          <Outlet />
        </main>
      </div>
    </AuthGuard>
  ),
})

const indexRoute = new Route({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({
      to: '/shift-timeline',
      replace: true,
    })
  },
})

const requestsRoute = new Route({
  getParentRoute: () => authenticatedRoute,
  path: '/requests',
  component: () => <RequestsPage />,
})

const timelineRoute = new Route({
  getParentRoute: () => authenticatedRoute,
  path: '/shift-timeline',
  component: () => <TimelinePage />,
})

const timelinePrototypeRoute = new Route({
  getParentRoute: () => authenticatedRoute,
  path: '/shift-timeline-prototype',
  component: () => <TimelinePrototypePage />,
})

const shiftsActivityLogRoute = new Route({
  getParentRoute: () => authenticatedRoute,
  path: '/shifts/activity-log',
  component: () => <ResourcesActivityLogPage />,
})

const stationManagerRoute = new Route({
  getParentRoute: () => authenticatedRoute,
  path: '/station-manager',
  component: () => <StationManagerPage />,
})

const shiftsCalendarRoute = new Route({
  getParentRoute: () => authenticatedRoute,
  path: '/shifts',
  component: () => <ShiftsCalendarPage />,
})

const shiftsDefaultRoute = new Route({
  getParentRoute: () => authenticatedRoute,
  path: '/shifts/default',
  validateSearch: (search: Record<string, unknown>) => ({
    on: typeof search.on === 'string' ? search.on : undefined,
  }),
  component: () => {
    const search = shiftsDefaultRoute.useSearch()
    return <ResourcesSchedulePage scheduleType="baseline" initialStartDate={search.on} />
  },
})

const shiftsOverrideRoute = new Route({
  getParentRoute: () => authenticatedRoute,
  path: '/shifts/override',
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
  }),
  component: () => {
    const search = shiftsOverrideRoute.useSearch()
    return (
      <ResourcesSchedulePage
        scheduleType="override"
        initialStartDate={search.from}
        initialEndDate={search.to}
      />
    )
  },
})

const callbackRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: CallbackPage,
})

const routeTree = rootRoute.addChildren([
  authenticatedRoute.addChildren([
    indexRoute,
    requestsRoute,
    timelineRoute,
    timelinePrototypeRoute,
    shiftsActivityLogRoute,
    stationManagerRoute,
    shiftsCalendarRoute,
    shiftsDefaultRoute,
    shiftsOverrideRoute,
  ]),
  callbackRoute,
])

export const router = new Router({ routeTree })
export { routeTree, rootRoute }

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
